import React from 'react';
import * as FormActions from '../../actions/actionCreators';
import '../Loader/loader.less';
import { useAppDispatch, useAppSelector } from '../../hooks';
import type { RootState } from '../../store';

type ModalProps = {
  action?: string;
  formType?: string;
};

const ModalFile = ({ document, idx }: { document: any; idx: number }) => (
  <div className="modal__file" key={idx}>
    <img
      className="modal__fileformat"
      src="/_resources/themes/bos/images/file.png"
      alt="File icon"
    />
    <p className="modal__filename">{document.file.name}</p>
  </div>
);

const Modal: React.FC<ModalProps> = ({ action, formType }) => {
  const dispatch = useAppDispatch();
  const forms = useAppSelector((state: RootState) => state.forms);
  const formStatus = useAppSelector(
    (state: RootState) => state.submit.formStatus
  );

  const handleSubmit = () => {
    if (formType === 'report') {
      dispatch(FormActions.submitReportForm(action));
    } else {
      dispatch(FormActions.submitCompleteForm(action));
    }
  };

  const handleDownload = () => {
    dispatch(FormActions.submitDownloadReport(action));
  };

  const handleStepEdit = (
    formName: string,
    index: number,
    formTypeInput: string
  ) => {
    dispatch(FormActions.editForm(formName, index, formTypeInput));
  };

  const handleCloseModal = () => {
    dispatch(FormActions.setShowPreview(false));
  };

  const renderButton = () => (
    <div className="modal__buttons-holder">
      {formStatus === 'loading' ? (
        <div className="loader" />
      ) : (
        <>
          <button
            type="submit"
            disabled={formStatus === 'loading'}
            className="modal__button button button--primary mr--2"
            onClick={handleSubmit}
          >
            Submit {formType === 'report' ? 'concerns' : formType}
          </button>
          {formType === 'report' && (
            <button
              type="submit"
              className="modal__button button button--secondary"
              onClick={handleDownload}
            >
              Download submission
            </button>
          )}
          {formStatus === 'error' && (
            <p>Oops! There was an error. Please try resubmitting the form.</p>
          )}
        </>
      )}
    </div>
  );

  const renderStepFields = (formName: string) =>
    Object.values(forms[formName]).map((field: any, index: number) => {
      if (typeof field === 'object') {
        if (Array.isArray(field.value)) {
          const hasDocuments = field.value.length > 0;

          return (
            <div key={index} className="modal__fileholder">
              {hasDocuments ? (
                <>
                  {field.value.map((document, idx) => (
                    <ModalFile document={document} idx={idx} />
                  ))}
                </>
              ) : (
                'You didn’t upload any documents'
              )}
            </div>
          );
        }
        return field.value ? (
          <div className="modal__data" key={index}>
            <p className="modal__label">{field.label}</p>
            <p className="modal__value">{field.value || 'No title'}</p>
          </div>
        ) : null;
      }
      return null;
    });

  const renderSteps = () =>
    Object.keys(forms).map((formName) => {
      const form = forms[formName];
      if (form.isHidden || !formType || form.type !== formType) return null;

      return (
        <div key={form.index} className="clearfix">
          <div className="modal__section">
            <h3 className="modal__title">{form.sectionHeading}</h3>
            <button
              type="button"
              className="modal__edit"
              onClick={() => handleStepEdit(formName, form.index, formType!)}
            >
              Edit
            </button>
          </div>
          {renderStepFields(formName)}
        </div>
      );
    });

  return (
    <div className="modal">
      <div className="grid--16 grid__md--14 grid__xl--10 grid--center">
        <div className="modal__inner">
          <div className="modal__header">
            <button
              type="button"
              className="modal__close"
              title="Close"
              aria-label="Close modal"
              onClick={(e) => handleCloseModal()}
            >
              <svg
                className="modal__icon"
                width="18"
                height="18"
                viewBox="0 0 10 10"
              >
                <g
                  stroke="#333"
                  strokeWidth="2.56"
                  fill="none"
                  strokeLinecap="round"
                >
                  <path d="M1.333 1.333l7.008 7.008M8.667 1.333L1.659 8.341" />
                </g>
              </svg>
            </button>
            <h2 className="modal__heading mt--0">
              Please review your form before submitting
            </h2>
            {formType !== 'report' && (
              <img
                className="modal__image"
                src="/_resources/themes/bos/images/desk.jpg"
                alt="Blue character at a desk, using a phone"
              />
            )}
          </div>
          {renderSteps()}
          {renderButton()}
        </div>
      </div>
    </div>
  );
};

export default Modal;
